import { Card } from '../components/ui/card';
import { Shield, Lock, Key, Eye, Server, AlertTriangle } from 'lucide-react';

export const Security = () => {
  const securityFeatures = [
    {
      icon: <Lock className="w-8 h-8" />,
      title: 'Data Encryption',
      description: 'All data transmitted between your device and our servers is encrypted using TLS 1.3 encryption.',
    },
    {
      icon: <Key className="w-8 h-8" />,
      title: 'Secure Authentication',
      description: 'We use industry-standard authentication protocols with secure password hashing and optional two-factor authentication.',
    },
    {
      icon: <Server className="w-8 h-8" />,
      title: 'Secure Infrastructure',
      description: 'Our servers are hosted in SOC 2 compliant data centers with 24/7 monitoring and regular security audits.',
    },
    {
      icon: <Eye className="w-8 h-8" />,
      title: 'Privacy by Design',
      description: 'We collect only necessary data and implement strict access controls to protect your information.',
    },
  ];

  const sections = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Our Security Commitment',
      content: `At Fudco, security is a top priority. We are committed to:
      • Protecting your personal and payment information
      • Implementing industry-leading security measures
      • Regularly testing and updating our security systems
      • Training our team on security best practices
      • Complying with applicable data protection regulations`,
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: 'Payment Security',
      content: `Your payment information is secured through:
      • PCI DSS compliant payment processing
      • Tokenization of card details (we never store full card numbers)
      • Secure payment gateways with fraud detection
      • Real-time transaction monitoring
      • Encrypted checkout process`,
    },
    {
      icon: <AlertTriangle className="w-6 h-6" />,
      title: 'Reporting Security Issues',
      content: `If you discover a security vulnerability or suspicious activity:
      • Email us immediately at service@fudco.in
      • Do not publicly disclose the vulnerability
      • Provide detailed information about the issue
      • Allow us reasonable time to address the problem
      
We appreciate responsible disclosure and may recognize security researchers who help us improve.`,
    },
  ];

  const tips = [
    'Use a strong, unique password for your Fudco account',
    'Never share your login credentials with anyone',
    'Log out of shared or public devices after use',
    'Keep your app updated to the latest version',
    'Be cautious of phishing emails or fake websites',
    'Enable notifications to monitor account activity',
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="security-page">
      {/* Hero */}
      <section className="fudco-hero-gradient fudco-section">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-green-100 text-[#2D8B2E] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Shield className="w-4 h-4" />
            Security
          </div>
          <h1 className="fudco-heading-1 text-gray-900 mb-6">
            Your Security is Our <span className="text-[#2D8B2E]">Priority</span>
          </h1>
          <p className="fudco-body-lg">
            Learn how we protect your data and what you can do to stay safe.
          </p>
        </div>
      </section>

      {/* Security Features */}
      <section className="fudco-section bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="fudco-heading-2 text-gray-900 text-center mb-12">How We Protect You</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {securityFeatures.map((feature, index) => (
              <Card key={index} className="fudco-card text-center hover-lift" data-testid={`security-feature-${index}`}>
                <div className="bg-green-100 text-[#2D8B2E] p-4 rounded-2xl inline-block mb-4">
                  {feature.icon}
                </div>
                <h3 className="fudco-heading-3 text-gray-900 mb-3">{feature.title}</h3>
                <p className="fudco-body">{feature.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Sections */}
      <section className="fudco-section bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-8">
            {sections.map((section, index) => (
              <Card key={index} className="p-8" data-testid={`security-section-${index}`}>
                <div className="flex items-center gap-4 mb-4">
                  <div className="bg-green-100 text-[#2D8B2E] p-3 rounded-xl">
                    {section.icon}
                  </div>
                  <h2 className="fudco-heading-3 text-gray-900">{section.title}</h2>
                </div>
                <div className="text-gray-600 whitespace-pre-line">{section.content}</div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Security Tips */}
      <section className="fudco-section bg-white">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 md:p-12 bg-gradient-to-br from-green-50 to-white border-green-100" data-testid="security-tips-card">
            <div className="flex items-center gap-4 mb-6">
              <div className="bg-[#2D8B2E] text-white p-4 rounded-2xl">
                <Key className="w-8 h-8" />
              </div>
              <h2 className="fudco-heading-2 text-gray-900">Security Tips for You</h2>
            </div>
            <ul className="space-y-4">
              {tips.map((tip, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="bg-green-100 text-[#2D8B2E] p-1 rounded-full mt-0.5">
                    <Shield className="w-4 h-4" />
                  </div>
                  <span className="text-gray-600">{tip}</span>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </section>

      {/* Contact */}
      <section className="bg-[#2D8B2E] fudco-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="fudco-heading-2 text-white mb-4">Found a Security Issue?</h2>
          <p className="text-green-100 text-lg mb-4">
            Report it to us immediately and help keep Fudco safe for everyone.
          </p>
          <p className="text-[#FFD700] font-semibold text-xl">service@fudco.in</p>
        </div>
      </section>
    </div>
  );
};
